import { invariantResponse } from '@epic-web/invariant'
import {
	Form,
	Link,
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
	useSearchParams,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import TrailCards from '#app/components/trail-cards.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { SimplePagination } from '#app/components/ui/pagination.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { queryCache } from '#app/utils/query-cache.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'

export const meta: MetaFunction<typeof loader> = ({ loaderData, location }) => {
	const displayName = loaderData?.owner.name ?? loaderData?.owner.username
	const metadata: any[] = [
		{ title: `${displayName} | ${import.meta.env.VITE_APP_TITLE}` },
		{
			name: 'description',
			content: `Profile of ${displayName} on ${import.meta.env.VITE_APP_TITLE}`,
		},
		{
			tagName: 'link',
			rel: 'canonical',
			href: `https://www.trailviewr.com${location.pathname}`,
		},
		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'Person',
				name: displayName,
				url: `https://www.trailviewr.com${location.pathname}`,
				image: loaderData?.owner.image?.id
					? `https://www.trailviewr.com${getUserImgSrc(loaderData.owner.image.id)}`
					: undefined,
				description: `Profile of ${displayName}`,
			},
		},
	]

	return metadata
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const username = params.username!
	const owner = await prisma.user.findUnique({
		where: { username },
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true } },
		},
	})
	invariantResponse(owner, 'User not found', { status: 404 })

	const joined = owner.createdAt.toLocaleDateString()

	const url = new URL(request.url)
	const currentPage = parseInt(url.searchParams.get('page') ?? '1', 10)
	const itemsPerPage = 12

	const totalCount = await queryCache.getCachedCount(
		`user-${owner.id}-captures-count`,
		() =>
			prisma.trailCapture.count({
				where: { ownerId: owner.id },
			}),
	)
	const totalPages = Math.ceil(totalCount / itemsPerPage)

	const captures = await prisma.trailCapture.findMany({
		where: { ownerId: owner.id },
		select: {
			id: true,
			slug: true,
			trailId: true,
			defaultFrameId: true,
			description: true,
			tags: {
				select: {
					tag: { select: { id: true, slug: true, name: true } },
				},
			},
		},
		orderBy: { createdAt: 'desc' },
		skip: (currentPage - 1) * itemsPerPage,
		take: itemsPerPage,
	})

	const trailIds = Array.from(new Set(captures.map((c) => c.trailId)))
	const trails = await prisma.trail.findMany({
		where: { id: { in: trailIds } },
		select: {
			id: true,
			slug: true,
			name: true,
			country: true,
			region: true,
			details: true,
			length: true,
			elevation_gain: true,
			latitude: true,
			longitude: true,
		},
	})
	const trailsById = Object.fromEntries(trails.map((t) => [t.id, t]))

	return { owner, joined, captures, trailsById, currentPage, totalPages }
}

export default function ProfileAndCaptures() {
	const { owner, joined, captures, trailsById, currentPage, totalPages } =
		useLoaderData<typeof loader>()
	const [searchParams, setSearchParams] = useSearchParams()
	const me = useOptionalUser()
	const isSelf = me?.id === owner.id
	const displayName = owner.name ?? owner.username

	function pageChangeHandler(newPage: number) {
		const params = new URLSearchParams(searchParams)
		if (newPage === 1) params.delete('page')
		else params.set('page', String(newPage))
		setSearchParams(params)
	}

	return (
		<div className="flex flex-1 flex-col overflow-hidden">
			<div className="mb-4 flex w-full flex-col items-center rounded-3xl bg-muted p-4 sm:mb-6 sm:p-6 md:mb-8 md:p-8 lg:p-12">
				<img
					src={getUserImgSrc(owner.image?.id)}
					alt={displayName}
					className="mb-4 h-32 w-32 rounded-full object-cover sm:h-40 sm:w-40 md:h-48 md:w-48 lg:h-52 lg:w-52"
				/>
				<h1 className="text-center text-h2">{displayName}</h1>
				<p className="mt-2 text-center text-muted-foreground">
					Joined {joined}
				</p>

				{isSelf && (
					<>
						<Form action="/logout" method="POST" className="mt-4">
							<Button type="submit" variant="link" size="icon">
								<Icon name="exit" className="scale-125 max-md:scale-150">
									Logout
								</Icon>
							</Button>
						</Form>
						<Spacer size="3xs" />
						<div className="flex flex-wrap justify-center gap-4">
							<Button asChild>
								<Link to="new">New Capture</Link>
							</Button>
							<Button asChild>
								<Link to="/settings/profile">Edit Profile</Link>
							</Button>
						</div>
					</>
				)}
			</div>
			<h2 className="mb-4 text-2xl leading-none font-extrabold tracking-tight text-gray-900 md:text-4xl dark:text-white">
				{displayName} Captures
			</h2>
			{captures.length > 0 ? (
				<>
					<TrailCards
						className="grid-cols grid flex-wrap gap-2 gap-y-0 sm:grid-cols-2 md:gap-4 lg:grid-cols-3 2xl:grid-cols-4"
						captures={captures}
						trailsById={trailsById}
						showCaptureDescription={true}
					/>
					<SimplePagination
						className="py-4 sm:py-6 md:py-8 lg:py-12"
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={pageChangeHandler}
					/>
				</>
			) : (
				<div className="my-8 text-center text-body-lg text-muted-foreground">
					{isSelf ? (
						<>
							You haven’t added any captures yet.
							<Spacer size="3xs" />
							<Button asChild variant="secondary">
								<Link to="new">Add one now!</Link>
							</Button>
						</>
					) : (
						`${displayName} hasn’t added any captures yet.`
					)}
				</div>
			)}
		</div>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username “{params.username}” exists.</p>
				),
			}}
		/>
	)
}
